import { ChatAttributeFilters } from "@/components/ui/chat/chatFilterUtils";
import { create } from "@/lib/zustand";
import { TMP_CONVERSATION_KEY } from "@/models/conversation";
import { VocPeriodFilterType } from "@/models/vocPeriodFilter";

type ConversationState = {
  isWaitingForAssistantMessage: boolean;
  content: string;
  categories: string[];
  selectedMessageId: string | null;
  vocPeriodStart: Date | null;
  vocPeriodEnd: Date | null;
  vocPeriodType: VocPeriodFilterType;
  attributeFilters: ChatAttributeFilters;
  tagIds: string[];
};

interface ChatState {
  currentConversationId: string;
  conversationStates: Record<string, ConversationState>;
  setConversationState: (conversationId: string, state: Partial<ConversationState>) => void;
  setCurrentConversationState: (state: Partial<ConversationState>) => void;
  setCurrentConversationId: (currentConversationId: string) => void;
  getCurrentConversationState: () => ConversationState;
}

export const initialConversationState: ConversationState = {
  isWaitingForAssistantMessage: false,
  content: "",
  categories: [],
  selectedMessageId: null,
  vocPeriodStart: null,
  vocPeriodEnd: null,
  vocPeriodType: VocPeriodFilterType.ALL,
  attributeFilters: {},
  tagIds: [],
};

export const useChatStore = create<ChatState>()(
  (set, get) => ({
    currentConversationId: TMP_CONVERSATION_KEY,
    conversationStates: {
      [TMP_CONVERSATION_KEY]: initialConversationState,
    },
    setConversationState: (conversationId: string, state: Partial<ConversationState>) =>
      set((prev) => ({
        conversationStates: {
          ...prev.conversationStates,
          [conversationId]: {
            ...initialConversationState,
            ...prev.conversationStates[conversationId],
            ...state,
          },
        },
      })),
    setCurrentConversationState: (state: Partial<ConversationState>) =>
      set((prev) => ({
        conversationStates: {
          ...prev.conversationStates,
          [prev.currentConversationId]: {
            ...initialConversationState,
            ...prev.conversationStates[prev.currentConversationId],
            ...state,
          },
        },
      })),
    setCurrentConversationId: (currentConversationId: string) => set(() => ({ currentConversationId })),
    getCurrentConversationState: () => {
      const state = get();
      return state.conversationStates[state.currentConversationId] ?? initialConversationState;
    },
  }),
);
